import { useTokens } from '@reservoir0x/reservoir-kit-ui'
import {
  Flex,
  FormatCryptoCurrency,
  Text,
  Tooltip,
} from 'components/primitives'
import FormatUSDCurrency from 'components/primitives/FormatUSDCurrency'
import { FC, useContext, useState } from 'react'
import FormatCurrencyDisplay from 'components/primitives/FormatCurrencyDisplay'
import { PriceDisplayContext } from 'context/PriceDisplayContextProvider'
import { PriceDisplay } from 'utils/priceDisplay'
import { getReservoirChainFromContract } from 'config/routes'

type Props = {
  token: ReturnType<typeof useTokens>['data'][0]
  systemId: string
}

export const PriceData: FC<Props> = ({ token, systemId }) => {
  const [isListLogoError, setIsListLogoError] = useState(false)
  const [isOfferLogoError, setIsOfferLogoError] = useState(false)
  const { priceDisplay } = useContext(PriceDisplayContext)

  const marketplaceChain = getReservoirChainFromContract(
    token?.token?.contract || ''
  )
  const proxyApi = marketplaceChain?.proxyApi || ''

  const listSourceName = token?.market?.floorAsk?.source?.name as
    | string
    | undefined
  const listSourceDomain = token?.market?.floorAsk?.source?.domain as
    | string
    | undefined

  const offerSourceName = token?.market?.topBid?.source?.name as
    | string
    | undefined
  const offerSourceDomain = token?.market?.topBid?.source?.domain as
    | string
    | undefined

  const listSourceLogo = `${
    process.env.NEXT_PUBLIC_PROXY_URL
  }${proxyApi}/redirect/sources/${listSourceDomain || listSourceName}/logo/v2`

  const offerSourceLogo = `${
    process.env.NEXT_PUBLIC_PROXY_URL
  }${proxyApi}/redirect/sources/${offerSourceDomain || offerSourceName}/logo/v2`

  const listSourceRedirect = `${
    process.env.NEXT_PUBLIC_PROXY_URL
  }${proxyApi}/redirect/sources/${listSourceDomain || listSourceName}/tokens/${
    token?.token?.contract
  }:${token?.token?.tokenId}/link/v2`

  const offerSourceRedirect = `${
    process.env.NEXT_PUBLIC_PROXY_URL
  }${proxyApi}/redirect/sources/${
    offerSourceDomain || offerSourceName
  }/tokens/${token?.token?.contract}:${token?.token?.tokenId}/link/v2`

  return (
    <Flex css={{ gap: '$6', pt: '$5', pb: '$5' }}>
      <Flex direction="column" align="start" css={{ gap: '$1' }}>
        <Text style="subtitle2">Price</Text>
        <Flex
          align="center"
          css={{
            flexDirection: 'column',
            '@bp400': { flexDirection: 'row', gap: '$2' },
          }}
        >
          <FormatCurrencyDisplay
            amount={token?.market?.floorAsk?.price?.amount}
            address={token?.market?.floorAsk?.price?.currency?.contract}
            decimals={token?.market?.floorAsk?.price?.currency?.decimals}
            textStyle="h4"
            logoHeight={20}
          />
          <Text style="body3" css={{ color: '$gray11' }} ellipsify>
            {token?.market?.floorAsk?.price?.amount &&
            priceDisplay === PriceDisplay.USD ? (
              <FormatCryptoCurrency
                amount={token?.market?.floorAsk?.price?.amount?.decimal}
              />
            ) : (
              <FormatUSDCurrency
                amount={token?.market?.floorAsk?.price?.amount?.usd}
              />
            )}
          </Text>
        </Flex>
        {listSourceName && !isListLogoError && (
          <a
            href={listSourceRedirect}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Flex
              align="center"
              css={{
                borderRadius: 4,
                gap: '$1',
                width: 'max-content',
              }}
            >
              <img
                width="20px"
                height="20px"
                src={listSourceLogo}
                onError={() => setIsListLogoError(true)}
              />
              <Text style="body3" css={{ color: '$gray11' }}>
                {listSourceName}
              </Text>
            </Flex>
          </a>
        )}
      </Flex>
      <Flex direction="column" align="start" css={{ gap: '$1' }}>
        <Text style="subtitle2">Top Offer</Text>
        <Flex
          align="center"
          css={{
            flexDirection: 'column',
            '@bp400': { flexDirection: 'row', gap: '$2' },
          }}
        >
          <Tooltip
            side="top"
            open={
              token?.market?.topBid?.price?.netAmount?.decimal
                ? undefined
                : false
            }
            content={
              <Flex justify="between" css={{ gap: '$2' }}>
                <Text style="body3">Net Amount</Text>
                <FormatCurrencyDisplay
                  amount={token?.market?.topBid?.price?.netAmount}
                  address={token?.market?.topBid?.price?.currency?.contract}
                  decimals={token?.market?.topBid?.price?.currency?.decimals}
                  textStyle="subtitle3"
                  logoHeight={14}
                />
              </Flex>
            }
          >
            <Flex>
              <FormatCurrencyDisplay
                amount={token?.market?.topBid?.price?.amount}
                address={token?.market?.topBid?.price?.currency?.contract}
                decimals={token?.market?.topBid?.price?.currency?.decimals}
                textStyle="h4"
                logoHeight={20}
              />
            </Flex>
          </Tooltip>
          <Text style="body3" css={{ color: '$gray11' }} ellipsify>
            {token?.market?.topBid?.price?.amount &&
            priceDisplay === PriceDisplay.USD ? (
              <FormatCryptoCurrency
                amount={token?.market?.topBid?.price?.amount?.decimal}
              />
            ) : (
              <FormatUSDCurrency
                amount={token?.market?.topBid?.price?.amount?.usd}
              />
            )}
          </Text>
        </Flex>
        {offerSourceName && !isOfferLogoError && (
          <a
            href={offerSourceRedirect}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Flex
              align="center"
              css={{
                borderRadius: 4,
                gap: '$1',
                width: 'max-content',
              }}
            >
              <img
                width="20px"
                height="20px"
                src={offerSourceLogo}
                onError={() => setIsOfferLogoError(true)}
              />
              <Text style="body3" css={{ color: '$gray11' }}>
                {offerSourceName}
              </Text>
            </Flex>
          </a>
        )}
      </Flex>
    </Flex>
  )
}
